/** P3-A12/PP12 — the receiving side of `src/sw.ts`'s `notificationclick`
 * handler (Task 5 built the sending side).
 *
 * `notificationclick` hands navigation to the running app in one of two
 * ways: a focused/open client gets `client.postMessage({type:'NAVIGATE',
 * url})` (never `client.navigate()` — a full navigate() would drop
 * in-memory SPA state); with no client open, it opens a fresh window at
 * `/?pushRedirect=<encoded path>` instead.
 *
 * `main.tsx` (where the service-worker registration lives) is mounted
 * OUTSIDE react-router's `<BrowserRouter>` — it renders `<App/>`, which owns
 * the Router — so it cannot call `useNavigate()` directly when the SW
 * message arrives. Instead it re-dispatches the SW's postMessage as a plain
 * DOM CustomEvent (`SW_NAVIGATE_EVENT`) that a Router-scoped hook
 * (`useSwNavigationBridge`, mounted inside `<BrowserRouter>` in App.tsx) can
 * subscribe to with `useNavigate()`. The `?pushRedirect=` query-param path
 * needs no such relay — that hook reads it directly via `useLocation()`.
 */
export const SW_NAVIGATE_EVENT = 'valentine:sw-navigate';

const PUSH_REDIRECT_PARAM = 'pushRedirect';
const PUSH_REDIRECT_SESSION_KEY = 'valentine:push-redirect-pending';

/** Fix-batch item 1 (Task-6 review) — guards every navigate() target this
 * bridge ever hands to react-router. The SW's `?pushRedirect=` query param
 * and `NAVIGATE` postMessage are both attacker-reachable via a crafted
 * link/notification payload (e.g. a link to
 * `https://valentine.example/?pushRedirect=https://evil.com`). Only a
 * same-origin relative path is accepted: a single leading slash (not a
 * protocol-relative `//`), no scheme prefix (`javascript:`, `https:`, ...),
 * and no backslash — some browsers normalize a leading `/\` to `//` and
 * treat it as protocol-relative too. Anything else either open-redirects
 * off Valentine or throws an uncaught SecurityError inside react-router's
 * history (blank-screen crash — nothing sits above this hook to catch it).
 */
export function isSafeInternalPath(path: unknown): path is string {
  if (typeof path !== 'string' || !path) return false;
  if (path.includes('\\')) return false;
  if (/^[a-zA-Z+.-]+:/.test(path)) return false;
  return /^\/(?!\/)/.test(path);
}

/** Fix-batch item 2 — `notificationclick` can postMessage a NAVIGATE before
 * React has mounted: the SW's `matchAll({includeUncontrolled: true})` counts
 * a still-loading tab as a client, so the `window` CustomEvent below can be
 * dispatched to zero listeners and silently lost. Stash the last URL at the
 * module level (mirrors `useInstallPrompt`'s `beforeinstallprompt` stash) so
 * `useSwNavigationBridge` can consume-and-clear it once it finally mounts.
 */
let stashedNavigateUrl: string | null = null;

export function consumeStashedNavigateUrl(): string | null {
  const url = stashedNavigateUrl;
  stashedNavigateUrl = null;
  return url;
}

export function forwardServiceWorkerNavigation(event: MessageEvent): void {
  const data = event.data as { type?: string; url?: string } | undefined;
  if (data?.type === 'NAVIGATE' && typeof data.url === 'string' && data.url) {
    stashedNavigateUrl = data.url;
    window.dispatchEvent(new CustomEvent<string>(SW_NAVIGATE_EVENT, { detail: data.url }));
  }
}

export function registerServiceWorkerNavigationBridge(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !navigator.serviceWorker) return;
  navigator.serviceWorker.addEventListener('message', forwardServiceWorkerNavigation);
}

/** Fix-batch item 5 — a `?pushRedirect=` fresh window can land on an
 * unauthenticated staff member: `<BrowserRouter>` (and this bridge's own
 * hook) mounts only after auth resolves, in `App.tsx`, so the query param
 * would otherwise just be lost behind `<LoginPage/>`. Worse, a
 * Microsoft-tenant `loginRedirect()` navigates the tab away to
 * login.microsoftonline.com and back to `redirectUri` (always the bare
 * origin — see `msalConfig.ts`), which drops the query string entirely.
 * `sessionStorage` survives that same-tab round trip (it's how MSAL tracks
 * its own "interaction in progress" flag), so `AuthContext.login()` stashes
 * the validated target here right before triggering `loginRedirect()`, and
 * `useSwNavigationBridge` consumes it once auth resolves and the Router
 * mounts. Native email/password login never navigates away, so the
 * `?pushRedirect=` param stays directly readable in `location.search` too —
 * consuming the stash first is a harmless no-op there (nothing was stashed).
 *
 * Whole-branch review (Gemini M) — this only ever looked at
 * `location.search`, missing a SECOND way a redirect target can be pending:
 * a `notificationclick` NAVIGATE `postMessage` that arrived while the tab
 * was open but a token had just expired. That lands in the module-level
 * `stashedNavigateUrl` (fix-batch item 2, above) rather than the URL — an
 * in-memory JS variable that a same-tab `loginRedirect()` round trip simply
 * wipes out (unlike `sessionStorage`, it doesn't survive navigation). So a
 * push tap followed immediately by an MSAL redirect used to lose the target
 * entirely. Drain that stash here too, preferring it over the query string
 * when both are somehow present — it reflects the more recent user action
 * (a notification tap right now vs. a stale `?pushRedirect=` still sitting
 * in the URL from an earlier fresh-window open).
 */
export function stashPushRedirectForAuth(search: string): void {
  const fromNavigateStash = consumeStashedNavigateUrl();
  try {
    const fromQuery = new URLSearchParams(search).get(PUSH_REDIRECT_PARAM);
    const redirect = fromNavigateStash ?? fromQuery;
    if (redirect && isSafeInternalPath(redirect)) {
      sessionStorage.setItem(PUSH_REDIRECT_SESSION_KEY, redirect);
    }
  } catch {
    // sessionStorage can throw under storage-quota/private-mode lockdown —
    // losing the redirect target is a soft failure, never a crash.
  }
}

export function consumeStashedPushRedirect(): string | null {
  try {
    const value = sessionStorage.getItem(PUSH_REDIRECT_SESSION_KEY);
    if (value !== null) sessionStorage.removeItem(PUSH_REDIRECT_SESSION_KEY);
    return value;
  } catch {
    return null;
  }
}
